import React, { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { 
  Mic, 
  Volume2, 
  Camera, 
  Monitor,
  Check,
  X,
  Loader2,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  Wifi,
  Play,
  Maximize,
  AlertCircle
} from 'lucide-react';

function TestStatusItem({ 
  icon: Icon, 
  label, 
  status, 
  message, 
  onRetry 
}) {
  const getStatusIcon = () => {
    switch (status) {
      case 'Testing':
        return <Loader2 className="w-4 h-4 animate-spin text-primary" />;
      case 'Tested':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'Error':
        return <X className="w-4 h-4 text-destructive" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'Tested':
        return 'border-green-500/50 bg-green-500/5';
      case 'Error':
        return 'border-destructive/50 bg-destructive/5';
      case 'Testing':
        return 'border-primary/50 bg-primary/5';
      default:
        return 'border-border';
    }
  };

  return (
    <div className={cn(
      "flex items-center justify-between p-2.5 rounded-lg border transition-colors",
      getStatusColor()
    )}>
      <div className="flex items-center gap-2.5">
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
          status === 'Tested' ? "bg-green-500/10 text-green-500" :
          status === 'Error' ? "bg-destructive/10 text-destructive" :
          status === 'Testing' ? "bg-primary/10 text-primary" :
          "bg-muted text-muted-foreground"
        )}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm">{label}</p>
          {message && (
            <p className={cn(
              "text-xs truncate",
              status === 'Error' ? "text-destructive" : "text-muted-foreground"
            )}>
              {message}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {getStatusIcon()}
        {status === 'Error' && onRetry && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onRetry}
            className="h-7 px-2 text-xs"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}

function NetworkIndicator({ status, speed }) {
  const getStatusColor = () => {
    switch (status) {
      case 'good':
        return 'text-green-500';
      case 'fair':
        return 'text-yellow-500';
      case 'poor':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'good':
        return 'Good';
      case 'fair':
        return 'Moderate';
      case 'poor':
        return 'Poor';
      default:
        return 'Checking...';
    }
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50">
      <Wifi className={cn("w-4 h-4", getStatusColor())} />
      <span className="text-sm">
        Network: <span className={cn("font-medium", getStatusColor())}>
          {speed ? `${speed} Mbps` : '--'} ({getStatusLabel()})
        </span>
      </span>
    </div>
  );
}

function SystemCheckStep({ 
  videoRef,
  micStatus,
  speakerStatus,
  cameraStatus,
  screenShareStatus,
  micMessage,
  networkStatus,
  networkSpeed,
  isDesktop,
  onMicTest,
  onSpeakerTest,
  onCameraTest,
  onScreenShareTest,
  onBack,
  onContinue,
  autoTestsComplete,
  testsStarted,
  onStartTests
}) {
  const allTestsPassed = 
    micStatus === 'Tested' && 
    speakerStatus === 'Tested' && 
    cameraStatus === 'Tested' && 
    (isDesktop || screenShareStatus === 'Tested');

  const anyTestRunning = 
    micStatus === 'Testing' || 
    speakerStatus === 'Testing' || 
    cameraStatus === 'Testing' || 
    screenShareStatus === 'Testing';

  // Show pre-test screen if tests haven't started yet
  if (!testsStarted) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="p-4 sm:p-6">
          <div className="text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <Monitor className="w-7 h-7 text-primary" />
            </div>
            
            <div className="space-y-1">
              <h2 className="text-xl font-semibold">Test Your Setup</h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                We need to verify your camera, microphone, speaker, and screen sharing are working properly.
              </p>
            </div>

            {/* What will happen */}
            <div className="bg-muted/50 rounded-lg p-3 max-w-lg mx-auto text-left space-y-2">
              <h3 className="font-medium text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-primary" />
                What happens when you click "Start Tests"
              </h3>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Maximize className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                  <span>Your browser will enter <strong className="text-foreground">fullscreen mode</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Camera className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                  <span>You'll be asked to allow <strong className="text-foreground">camera & microphone</strong> access</span>
                </li>
                {!isDesktop && (
                  <li className="flex items-start gap-2">
                    <Monitor className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                    <span>You'll be asked to <strong className="text-foreground">share your screen</strong> (select "Entire Screen")</span>
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <Volume2 className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                  <span>A brief <strong className="text-foreground">test sound</strong> will play to verify your speakers</span>
                </li>
              </ul>
            </div>

            {/* Action buttons */}
            <div className="flex justify-center gap-3 pt-1">
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button onClick={onStartTests}>
                <Play className="w-4 h-4 mr-2" />
                Start System Checks
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardContent className="p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Camera Preview */}
          <div className="space-y-3">
            <h3 className="font-semibold text-base">Camera Preview</h3>
            <div className={cn(
              "relative aspect-video rounded-lg overflow-hidden border-2 bg-muted",
              cameraStatus === 'Tested' ? 'border-green-500' : 'border-border'
            )}>
              <video 
                ref={videoRef} 
                className="w-full h-full object-cover" 
                muted 
                playsInline 
                autoPlay
              />
              {cameraStatus !== 'Tested' && (
                <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
                  {cameraStatus === 'Testing' ? (
                    <div className="text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Accessing camera...</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Camera className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Camera preview will appear here</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <NetworkIndicator status={networkStatus} speed={networkSpeed} />
          </div>

          {/* System Checks */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-base">System Checks</h3>
              {anyTestRunning && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Running tests...
                </span>
              )}
            </div>
            
            <div className="space-y-2">
              <TestStatusItem
                icon={Camera}
                label="Camera"
                status={cameraStatus}
                message={
                  cameraStatus === 'Tested' ? 'Camera is working' :
                  cameraStatus === 'Error' ? 'Camera access failed' :
                  cameraStatus === 'Testing' ? 'Testing camera...' :
                  'Not tested yet'
                }
                onRetry={cameraStatus === 'Error' ? onCameraTest : null}
              />

              <TestStatusItem
                icon={Mic}
                label="Microphone"
                status={micStatus}
                message={
                  micStatus === 'Tested' ? (micMessage || 'Microphone is working') :
                  micStatus === 'Error' ? (micMessage || 'Microphone access failed') :
                  micStatus === 'Testing' ? 'Testing microphone...' :
                  'Not tested yet'
                }
                onRetry={micStatus === 'Error' ? onMicTest : null}
              />

              <TestStatusItem
                icon={Volume2}
                label="Speaker"
                status={speakerStatus}
                message={
                  speakerStatus === 'Tested' ? 'Speaker is working' :
                  speakerStatus === 'Error' ? 'Speaker test failed' :
                  speakerStatus === 'Testing' ? 'Playing test sound...' :
                  'Not tested yet'
                }
                onRetry={speakerStatus === 'Error' ? onSpeakerTest : null}
              />

              {!isDesktop && (
                <TestStatusItem
                  icon={Monitor}
                  label="Screen Share"
                  status={screenShareStatus}
                  message={
                    screenShareStatus === 'Tested' ? 'Screen sharing active' :
                    screenShareStatus === 'Error' ? 'Screen share permission denied' :
                    screenShareStatus === 'Testing' ? 'Requesting permission...' :
                    'Not tested yet'
                  }
                  onRetry={screenShareStatus === 'Error' ? onScreenShareTest : null}
                />
              )}
            </div>

            {/* Status summary */}
            <div className={cn(
              "p-2.5 rounded-lg text-sm",
              allTestsPassed 
                ? "bg-green-500/10 text-green-700 dark:text-green-400" 
                : "bg-muted text-muted-foreground"
            )}>
              {allTestsPassed ? (
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <span>All system checks passed! You're ready to continue.</span>
                </div>
              ) : anyTestRunning ? (
                <span>Please wait while we test your system...</span>
              ) : (
                <span>Some tests need to pass before you can continue.</span>
              )}
            </div>
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between pt-4 mt-4 border-t">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            onClick={onContinue} 
            disabled={!allTestsPassed || anyTestRunning}
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default SystemCheckStep;
